"use client";

import { useEffect, useRef, useState } from "react";
import Script from "next/script";
import { useLocale } from "next-intl";

const TURNSTILE_SITE_KEY = process.env.NEXT_PUBLIC_TURNSTILE_SITE_KEY || "";
const HCAPTCHA_SITE_KEY = process.env.NEXT_PUBLIC_HCAPTCHA_SITE_KEY || "";

declare global {
  interface Window {
    turnstile?: any;
    hcaptcha?: any;
  }
}

export default function CaptchaBox({
  onVerify,
  onExpired,
}: {
  onVerify: (token: string | null) => void;
  onExpired?: () => void;
}) {
  const [provider, setProvider] = useState<"turnstile" | "hcaptcha" | "none">(
    "none",
  );
  const widgetRef = useRef<HTMLDivElement>(null);
  const renderedRef = useRef(false);
  const tokenRef = useRef<string | null>(null);
  const [scriptReady, setScriptReady] = useState(false);
  const locale = useLocale();

  const lang = (locale || "en").toString();

  useEffect(() => {
    if (TURNSTILE_SITE_KEY) setProvider("turnstile");
    else if (HCAPTCHA_SITE_KEY) setProvider("hcaptcha");
    else setProvider("none");
  }, []);

  useEffect(() => {
    onVerify(tokenRef.current || null);
  }, [onVerify, provider]);

  // Register global callbacks once per provider so data-* can reference string names
  useEffect(() => {
    if (provider === "turnstile") {
      (window as any).tmTurnstileCallback = (t: string) => {
        tokenRef.current = t;
        onVerify(t);
      };
      (window as any).tmTurnstileExpired = () => {
        tokenRef.current = null;
        onVerify(null);
        onExpired && onExpired();
      };
    } else if (provider === "hcaptcha") {
      (window as any).tmHcaptchaCallback = (t: string) => {
        tokenRef.current = t;
        onVerify(t);
      };
      (window as any).tmHcaptchaExpired = () => {
        tokenRef.current = null;
        onVerify(null);
        onExpired && onExpired();
      };
    }
  }, [onExpired, onVerify, provider, scriptReady]);

  // When script is already present (client-side navigation), explicitly render widget
  useEffect(() => {
    if (!widgetRef.current || renderedRef.current) return;
    // Avoid re-render if widget already exists under container
    if (widgetRef.current.childElementCount > 0) {
      renderedRef.current = true;
      return;
    }
    if (
      provider === "turnstile" &&
      typeof window !== "undefined" &&
      (window as any).turnstile?.render
    ) {
      try {
        (window as any).turnstile.render(widgetRef.current, {
          sitekey: TURNSTILE_SITE_KEY,
          callback: (t: string) => {
            tokenRef.current = t;
            onVerify(t);
          },
          "expired-callback": () => {
            tokenRef.current = null;
            onVerify(null);
            onExpired && onExpired();
          },
          language: lang,
        });
        renderedRef.current = true;
      } catch {}
    }
    if (
      provider === "hcaptcha" &&
      typeof window !== "undefined" &&
      (window as any).hcaptcha?.render
    ) {
      try {
        (window as any).hcaptcha.render(widgetRef.current, {
          sitekey: HCAPTCHA_SITE_KEY,
          callback: (t: string) => {
            tokenRef.current = t;
            onVerify(t);
          },
          "expired-callback": () => {
            tokenRef.current = null;
            onVerify(null);
            onExpired && onExpired();
          },
          language: lang,
        });
        renderedRef.current = true;
      } catch {}
    }
  }, [provider, lang, onVerify, onExpired, scriptReady]);

  // Render
  if (provider === "turnstile") {
    return (
      <>
        <Script
          src={`https://challenges.cloudflare.com/turnstile/v0/api.js?hl=${encodeURIComponent(lang)}`}
          strategy="afterInteractive"
          onLoad={() => {
            renderedRef.current = false;
            setScriptReady(true);
          }}
        />
        <div ref={widgetRef} />
      </>
    );
  }

  if (provider === "hcaptcha") {
    return (
      <>
        <Script
          src={`https://hcaptcha.com/1/api.js?hl=${encodeURIComponent(lang)}`}
          strategy="afterInteractive"
          onLoad={() => {
            renderedRef.current = false;
            setScriptReady(true);
          }}
        />
        <div ref={widgetRef} />
      </>
    );
  }

  // No provider configured
  return null;
}
